import { type FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  ChevronLeftIcon,
  ChevronRightIcon,
  Column,
  IconButton,
  Paragraph,
  Row,
  SegmentedControl,
  SegmentedControlOption,
  Skeleton,
  SkeletonBox,
  Spinner,
  Text,
  TextWithTooltip,
  HtImage,
} from "@hightouch/htui";
import {
  DOWNLOAD_OPTION_META,
  type ImageFileType,
  JPG_QUALITY_OPTIONS,
  PNG_IMAGE_FILE_TYPE,
} from "src/pages/ad-studio/briefs/components/download-options";
import { applyVisibleDisclaimer } from "src/pages/ad-studio/html-ad-editor/apply-disclaimer";
import { captureContent } from "src/pages/ad-studio/html-ad-editor/capture-content";
import {
  DisclaimerControls,
  SingleAdDisclaimerControls,
} from "./disclaimer-controls";
import {
  type DisclaimerOverride,
  applyOverride,
  resolveFieldState,
} from "./disclaimer-override";
import type {
  ExportSettingsArgs,
  ExportSettingsState,
  ResolvedPreviewItem,
} from "./export-settings";

type ExportSettingsBodyProps = ExportSettingsArgs & {
  state: ExportSettingsState;
};

/** Settings column on the left, disclosure preview on the right. */
export const ExportSettingsBody: FC<ExportSettingsBodyProps> = ({
  staticAssets,
  videoAssets,
  state,
}) => {
  const {
    resolvedItems,
    offeredFormats,
    override,
    setOverride,
    selectedOptions,
    toggleOption,
    imageFileType,
    setImageFileType,
    pdfImageFileType,
    setPdfImageFileType,
    videoSpec,
    setVideoSpec,
    showFormats,
    showStaticDisclosures,
    showImageSettings,
    showVideoSettings,
  } = state;
  const singleFormat =
    offeredFormats.length === 1 ? offeredFormats[0] : undefined;

  return (
    <Box
      display="grid"
      gridTemplateColumns={
        showStaticDisclosures ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)"
      }
      gap={6}
    >
      <Column gap={6}>
        {showImageSettings && staticAssets && (
          <Column gap={4}>
            <Text fontWeight="semibold" size="lg">
              Image settings
            </Text>
            {singleFormat && (
              <Column gap={3}>
                <Column gap={1}>
                  <Text fontWeight="medium">
                    {DOWNLOAD_OPTION_META[singleFormat].label}
                  </Text>
                  <Text color="text.secondary">
                    {DOWNLOAD_OPTION_META[singleFormat].description}
                  </Text>
                </Column>
                {singleFormat === "images" && (
                  <ImageFileTypeControls
                    value={imageFileType}
                    onChange={setImageFileType}
                    fileTypeLabel="Image file type"
                    qualityLabel="JPG quality"
                    isIndented={false}
                  />
                )}
                {singleFormat === "pdf" && (
                  <ImageFileTypeControls
                    value={pdfImageFileType}
                    onChange={setPdfImageFileType}
                    fileTypeLabel="PDF page file type"
                    qualityLabel="PDF JPG quality"
                    isIndented={false}
                  />
                )}
              </Column>
            )}
            {offeredFormats.length > 1 && (
              <Column gap={3}>
                <Text fontWeight="medium">Formats</Text>
                {offeredFormats.map((option) => (
                  <Column key={option} gap={3}>
                    <Checkbox
                      isChecked={selectedOptions.has(option)}
                      onChange={() => toggleOption(option)}
                      label={DOWNLOAD_OPTION_META[option].label}
                      description={DOWNLOAD_OPTION_META[option].description}
                    />
                    {option === "images" && selectedOptions.has("images") && (
                      <ImageFileTypeControls
                        value={imageFileType}
                        onChange={setImageFileType}
                        fileTypeLabel="Image file type"
                        qualityLabel="JPG quality"
                      />
                    )}
                    {option === "pdf" && selectedOptions.has("pdf") && (
                      <ImageFileTypeControls
                        value={pdfImageFileType}
                        onChange={setPdfImageFileType}
                        fileTypeLabel="PDF page file type"
                        qualityLabel="PDF JPG quality"
                      />
                    )}
                  </Column>
                ))}
              </Column>
            )}
            {showStaticDisclosures && (
              <Column gap={3}>
                {showFormats && <Text fontWeight="medium">AI disclosures</Text>}
                <StaticDisclosureControls
                  resolvedItems={resolvedItems}
                  override={override}
                  onOverrideChange={setOverride}
                />
              </Column>
            )}
          </Column>
        )}
        {showVideoSettings && videoAssets && (
          <Column
            gap={3}
            // A rule separates the two groups when both are present.
            borderTopWidth={showImageSettings ? "hairline" : undefined}
            borderColor="base.divider"
            pt={showImageSettings ? 6 : undefined}
          >
            <Text fontWeight="semibold" size="lg">
              Video settings
            </Text>
            <Paragraph color="text.secondary">
              {videoAssets.count === 1
                ? "Applies to the video you selected."
                : `Applies to all ${videoAssets.count} videos you selected.`}
            </Paragraph>
            <SingleAdDisclaimerControls
              value={videoSpec}
              onChange={setVideoSpec}
            />
          </Column>
        )}
        {staticAssets?.notice && (
          <Alert
            type="info"
            title={staticAssets.notice.title}
            message={staticAssets.notice.message}
          />
        )}
      </Column>
      {showStaticDisclosures && staticAssets && (
        // Sticks to the scrolling body so the preview stays in view while
        // the settings column scrolls past it.
        <Box position="sticky" top={0} alignSelf="start">
          <DisclosurePreview
            resolvedItems={resolvedItems}
            override={override}
            getFontRulesForMarkup={staticAssets.getFontRulesForMarkup}
            apiBase={staticAssets.apiBase}
          />
        </Box>
      )}
    </Box>
  );
};

const DEFAULT_JPG_QUALITY = JPG_QUALITY_OPTIONS[0].value;

const ImageFileTypeControls: FC<{
  value: ImageFileType;
  onChange: (value: ImageFileType) => void;
  /** Accessible names; the images and PDF controls can show at once. */
  fileTypeLabel: string;
  qualityLabel: string;
  /** Lines up under a checkbox label. */
  isIndented?: boolean;
}> = ({ value, onChange, fileTypeLabel, qualityLabel, isIndented = true }) => (
  <Row gap={4} pl={isIndented ? 6 : 0} align="center" flexWrap="wrap">
    <SegmentedControl<ImageFileType["type"]>
      aria-label={fileTypeLabel}
      value={value.type}
      onChange={(type) =>
        onChange(
          type === "jpg"
            ? { type: "jpg", quality: DEFAULT_JPG_QUALITY }
            : PNG_IMAGE_FILE_TYPE,
        )
      }
    >
      <SegmentedControlOption value="png" label="PNG" />
      <SegmentedControlOption value="jpg" label="JPG" />
    </SegmentedControl>
    {value.type === "jpg" && (
      <SegmentedControl<number>
        aria-label={qualityLabel}
        value={value.quality}
        onChange={(quality) => onChange({ type: "jpg", quality })}
      >
        {JPG_QUALITY_OPTIONS.map((option) => (
          <SegmentedControlOption
            key={option.value}
            value={option.value}
            label={option.label}
          />
        ))}
      </SegmentedControl>
    )}
  </Row>
);

type StaticDisclosureControlsProps = {
  resolvedItems: ResolvedPreviewItem[];
  override: DisclaimerOverride;
  onOverrideChange: (
    update: (override: DisclaimerOverride) => DisclaimerOverride,
  ) => void;
};

/** Mixed-state controls over every selected ad's resolved disclosure. */
const StaticDisclosureControls: FC<StaticDisclosureControlsProps> = ({
  resolvedItems,
  override,
  onOverrideChange,
}) => {
  const visibleState = resolveFieldState(
    resolvedItems.map((r) => r.spec.visible),
    override.visible,
  );
  const machineReadableState = resolveFieldState(
    resolvedItems.map((r) => r.spec.machineReadable),
    override.machineReadable,
  );
  const positionState = resolveFieldState(
    resolvedItems.map((r) => r.spec.position),
    override.position,
  );

  return (
    <DisclaimerControls
      visible={visibleState.value ?? false}
      visibleMixed={visibleState.mixed}
      machineReadable={machineReadableState.value ?? false}
      machineReadableMixed={machineReadableState.mixed}
      position={positionState.value ?? null}
      positionMixed={positionState.mixed}
      onVisibleChange={(value) =>
        onOverrideChange((o) => ({ ...o, visible: value }))
      }
      onMachineReadableChange={(value) =>
        onOverrideChange((o) => ({ ...o, machineReadable: value }))
      }
      onPositionChange={(value) =>
        onOverrideChange((o) => ({ ...o, position: value }))
      }
    />
  );
};

type DisclosurePreviewProps = {
  resolvedItems: ResolvedPreviewItem[];
  override: DisclaimerOverride;
  getFontRulesForMarkup: (markup: string) => Promise<string[]>;
  apiBase: string;
};

/** Preview carousel of the selected ads with the effective disclosures applied. */
const DisclosurePreview: FC<DisclosurePreviewProps> = ({
  resolvedItems,
  override,
  getFontRulesForMarkup,
  apiBase,
}) => {
  const [index, setIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  const total = resolvedItems.length;
  const safeIndex = Math.min(index, Math.max(0, total - 1));
  const currentResolved = resolvedItems[safeIndex] ?? null;
  const current = currentResolved?.item ?? null;
  const currentFont = currentResolved?.font;

  const currentEffectiveSpec = useMemo(
    () =>
      currentResolved ? applyOverride(currentResolved.spec, override) : null,
    [currentResolved, override],
  );

  // Re-render the preview whenever the effective disclosure or the selected ad
  // changes. Runs the real capture pipeline so the preview matches the
  // downloaded PNG exactly (minus the invisible metadata). Only the current
  // carousel slide is rendered; the previous preview stays mounted until the
  // replacement is ready, so the dialog doesn't flicker between renders.
  useEffect(() => {
    if (!current || !currentEffectiveSpec) return;

    let canceled = false;
    setIsPreviewLoading(true);
    setPreviewFailed(false);

    void (async () => {
      try {
        const content = currentEffectiveSpec.visible
          ? applyVisibleDisclaimer(
              current.content,
              currentEffectiveSpec,
              currentFont,
            )
          : current.content;
        const fontRules = await getFontRulesForMarkup(content);
        const blob = await captureContent({
          assetId: current.assetId ?? current.id,
          content,
          fontRules,
          componentAssets: current.componentAssets,
          apiBase,
        });
        if (canceled) return;
        const nextUrl = URL.createObjectURL(blob);
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = nextUrl;
        setPreviewUrl(nextUrl);
      } catch (err) {
        if (!canceled) setPreviewFailed(true);
        console.error("disclaimer preview: capture failed", err);
      } finally {
        if (!canceled) setIsPreviewLoading(false);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [
    current,
    currentEffectiveSpec,
    currentFont,
    getFontRulesForMarkup,
    apiBase,
  ]);

  // Revoke the last object URL when the preview unmounts.
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  if (!current) return null;

  return (
    <Column gap={2}>
      {!previewUrl && !previewFailed ? (
        // Sits outside the centering Box below, where a percentage height has
        // no definite parent and collapses to nothing.
        <Skeleton isLoading>
          <SkeletonBox height="md" width="100%" />
        </Skeleton>
      ) : (
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="md"
          p={2}
          bg="base.lightBackground"
          border="1px solid"
          borderColor="base.border"
          borderRadius="md"
          overflow="hidden"
        >
          {previewUrl ? (
            <HtImage
              src={previewUrl}
              alt="Ad preview with the selected disclosures applied"
              maxHeight="100%"
              maxWidth="100%"
              sx={{ objectFit: "contain" }}
            />
          ) : (
            <Text color="text.secondary">Preview unavailable</Text>
          )}
          {isPreviewLoading && previewUrl && (
            <Box position="absolute" top={2} right={2}>
              <Spinner size="sm" />
            </Box>
          )}
        </Box>
      )}

      {total > 1 && (
        <Row align="center" justify="space-between">
          <IconButton
            aria-label="Previous ad"
            icon={ChevronLeftIcon}
            variant="tertiary"
            isDisabled={safeIndex === 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
          />
          <TextWithTooltip size="sm" color="text.secondary">
            {current.label
              ? `${current.label} (${safeIndex + 1} of ${total})`
              : `${safeIndex + 1} of ${total}`}
          </TextWithTooltip>
          <IconButton
            aria-label="Next ad"
            icon={ChevronRightIcon}
            variant="tertiary"
            isDisabled={safeIndex >= total - 1}
            onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
          />
        </Row>
      )}
    </Column>
  );
};
